/**
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Detector for Flutter mobile and web applications
 */
import type { DetectorResult } from '../types/index.js';
import type { Detector } from './base.js';
import { fileExists } from '../utils/fs.js';
import { calculateConfidence, type ConfidenceFactors } from '../core/confidence.js';
import { createDetectorResult } from './base.js';
import { join } from 'path';

interface PubspecYaml {
  name?: string;
  dependencies?: Record<string, unknown>;
  dev_dependencies?: Record<string, unknown>;
  flutter?: {
    uses_material_design?: boolean;
  };
}

export class FlutterDetector implements Detector {
  name = 'flutter';

  async detect(cwd: string): Promise<DetectorResult> {
    const pubspecPath = join(cwd, 'pubspec.yaml');
    
    if (!fileExists(pubspecPath)) {
      return createDetectorResult(this.name, 0);
    }

    // Read pubspec.yaml
    const pubspec = await this.readPubspec(pubspecPath);
    if (!pubspec) {
      return createDetectorResult(this.name, 0);
    }

    // Check if it's a Flutter project
    const isFlutterProject = this.isFlutterProject(pubspec);
    if (!isFlutterProject) {
      return createDetectorResult(this.name, 0);
    }

    // Check for platform-specific indicators
    const hasAndroid = fileExists(join(cwd, 'android'));
    const hasIos = fileExists(join(cwd, 'ios'));
    const hasWeb = fileExists(join(cwd, 'web'));
    const hasMainDart = fileExists(join(cwd, 'lib', 'main.dart'));

    const factors: ConfidenceFactors = {
      frameworkDependency: 1,
      configFileExists: 1,
      entryPointExists: hasMainDart ? 1 : 0,
      multipleIndicators: (hasAndroid ? 1 : 0) + (hasIos ? 1 : 0) + (hasWeb ? 1 : 0) > 1 ? 1 : 0,
    };

    const confidence = calculateConfidence(factors);

    // Determine target platforms
    const platforms: string[] = [];
    if (hasAndroid) platforms.push('Android');
    if (hasIos) platforms.push('iOS');
    if (hasWeb) platforms.push('Web');
    if (platforms.length === 0) platforms.push('Mobile');

    const reasons = [
      'Detected Flutter project',
      `Platforms: ${platforms.join(', ')}`,
      'Using Flutter SDK',
    ];

    if (!hasMainDart) {
      reasons.push('⚠️  Warning: lib/main.dart not found');
    }

    // Detect if it's a web-focused project
    if (hasWeb && !hasAndroid && !hasIos) {
      return createDetectorResult(this.name, confidence, {
        cwd,
        pm: null,
        cmd: 'flutter',
        args: ['run', '-d', 'chrome'],
        detectedType: 'Flutter Web',
        reasons: [
          ...reasons,
          'Web-only configuration detected',
        ],
      });
    }

    // Default Flutter run command
    return createDetectorResult(this.name, confidence, {
      cwd,
      pm: null,
      cmd: 'flutter',
      args: ['run'],
      detectedType: 'Flutter',
      reasons,
    });
  }

  /**
   * Read and parse pubspec.yaml
   */
  private async readPubspec(path: string): Promise<PubspecYaml | null> {
    try {
      const fs = await import('fs/promises');
      const content = await fs.readFile(path, 'utf-8');
      
      // Simple YAML parsing for pubspec.yaml
      // Check for Flutter dependency
      const lines = content.split('\n');
      const pubspec: PubspecYaml = {
        dependencies: {},
        dev_dependencies: {},
      };

      let currentSection: 'dependencies' | 'dev_dependencies' | null = null;

      for (const line of lines) {
        const trimmed = line.trim();

        if (trimmed.startsWith('name:')) {
          pubspec.name = trimmed.split(':')[1]?.trim();
        }

        if (trimmed === 'dependencies:') {
          currentSection = 'dependencies';
        } else if (trimmed === 'dev_dependencies:') {
          currentSection = 'dev_dependencies';
        } else if (trimmed.startsWith('flutter:')) {
          currentSection = null;
          pubspec.flutter = {};
        }

        if (currentSection && trimmed.includes(':') && !trimmed.startsWith(currentSection)) {
          const [key] = trimmed.split(':');
          if (key && pubspec[currentSection]) {
            pubspec[currentSection]![key.trim()] = true;
          }
        }
      }

      return pubspec;
    } catch {
      return null;
    }
  }

  /**
   * Check if pubspec.yaml indicates a Flutter project
   */
  private isFlutterProject(pubspec: PubspecYaml): boolean {
    return Boolean(
      pubspec.dependencies?.flutter ||
      pubspec.dev_dependencies?.flutter ||
      pubspec.flutter
    );
  }
}

